import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';

import { DataListType } from '@models/module/fields/main';
import { GridSearchEntityInstance, SearchViewModel } from '@models/module/grid';
import { ScreenService } from '@module-definition/screen/common/screen.service';
import { isCompleteDateRange, toValidTime } from '@module-instance/gantt/common/gantt-date.util';
import { ModuleGridService } from '@module-instance/services';

interface CategorizedRows {
  completeRows: GridSearchEntityInstance[];
  startOnlyRows: GridSearchEntityInstance[];
  endOnlyRows: GridSearchEntityInstance[];
  noDateRows: GridSearchEntityInstance[];
}

@Injectable({ providedIn: 'root' })
export class GanttService {
  private moduleGridService = inject(ModuleGridService);
  private screenService = inject(ScreenService);

  getScreen(id: number) {
    return this.screenService.get(id);
  }

  getData(body: SearchViewModel, contractId: number, moduleId: number) {
    body.pagesize = 99999; // Ensure we get all data for Gantt
    body.isCurrent = true;
    body.showPresets = false;
    return this.moduleGridService
      .gridSearch({ body, contractId, moduleId, dataListType: DataListType.Gantt })
      .pipe(map(r => (r.data ?? []) as GridSearchEntityInstance[]));
  }

  /**
   * Escape HTML special characters to prevent XSS.
   */
  escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Format text as HTML with truncation and ellipsis.
   */
  formatTruncatedGridCell(text: string, widthPx: number): string {
    const safe = this.escapeHtml(text);
    return `<span style="display:block;width:${widthPx}px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${safe}">${safe}</span>`;
  }

  /**
   * Create a comparator function for sorting by start date.
   * Rows without a start date sort to the end (Infinity) to avoid NaN comparisons.
   */
  createSortByStartComparator(startKey: string): (a: GridSearchEntityInstance, b: GridSearchEntityInstance) => number {
    return (a: GridSearchEntityInstance, b: GridSearchEntityInstance) => {
      const aTime = toValidTime(a[startKey]) ?? Infinity;
      const bTime = toValidTime(b[startKey]) ?? Infinity;
      return aTime - bTime;
    };
  }

  /**
   * Categorize rows by their date availability (complete, start-only, end-only, no-dates).
   */
  categorizeRowsByDates(rows: GridSearchEntityInstance[], startKey: string, endKey: string): CategorizedRows {
    const completeRows: GridSearchEntityInstance[] = [];
    const startOnlyRows: GridSearchEntityInstance[] = [];
    const endOnlyRows: GridSearchEntityInstance[] = [];
    const noDateRows: GridSearchEntityInstance[] = [];

    for (const r of rows) {
      const startTime = toValidTime(r[startKey]);
      const endTime = toValidTime(r[endKey]);
      const hasStart = startTime != null;
      const hasEnd = endTime != null;
      if (hasStart && hasEnd && isCompleteDateRange(startTime, endTime)) {
        completeRows.push(r);
      } else if (hasStart && !hasEnd) {
        startOnlyRows.push(r);
      } else if (!hasStart && hasEnd) {
        endOnlyRows.push(r);
      } else {
        noDateRows.push(r);
      }
    }

    return { completeRows, startOnlyRows, endOnlyRows, noDateRows };
  }

  /**
   * Flatten a tree structure of rows, preserving parent-child relationships.
   * Orphaned rows (parent not in current result set) are treated as roots.
   * Cycle detection prevents infinite recursion.
   */
  flattenTree(
    items: GridSearchEntityInstance[],
    relKey: string,
    sortByStart: (a: GridSearchEntityInstance, b: GridSearchEntityInstance) => number,
  ): GridSearchEntityInstance[] {
    const itemIds = new Set(items.map(r => String(r.entity_instance_id)));
    const childrenByParent = new Map<string, GridSearchEntityInstance[]>();
    const rootItems: GridSearchEntityInstance[] = [];

    for (const item of items) {
      const parentRaw = item[relKey];
      const hasParent = parentRaw !== null && parentRaw !== undefined && parentRaw !== '';
      const parentId = hasParent ? String(parentRaw) : null;

      // Treat rows as roots when parent is empty or not present in the current result set.
      if (!parentId || !itemIds.has(parentId)) {
        rootItems.push(item);
        continue;
      }

      const siblings = childrenByParent.get(parentId) ?? [];
      siblings.push(item);
      childrenByParent.set(parentId, siblings);
    }

    // Sort each sibling group once up-front so walk reads pre-sorted arrays.
    for (const siblings of childrenByParent.values()) {
      siblings.sort(sortByStart);
    }

    // Walk the tree; a visited set prevents infinite loops caused by cyclic parent references.
    const walk = (parentId: string, visited: Set<string>): GridSearchEntityInstance[] => {
      const children = childrenByParent.get(parentId) ?? [];
      return children.flatMap(child => {
        const childId = String(child.entity_instance_id);
        if (visited.has(childId)) return [];
        visited.add(childId);
        return [child, ...walk(childId, visited)];
      });
    };

    const sortedRoots = rootItems.sort(sortByStart);
    return sortedRoots.flatMap(root => {
      const visited = new Set([String(root.entity_instance_id)]);
      return [root, ...walk(String(root.entity_instance_id), visited)];
    });
  }

  /** Span the x-axis to include every bar, with light edge padding. */
  computeXAxisRange(
    ganttData: { start?: string | number; end?: string | number }[],
    now: number,
    oneDay: number,
    twoWeeks: number,
  ): { min: number; max: number } {
    const toTime = (value: string | number | undefined): number | null => {
      if (value == null) return null;
      const time = typeof value === 'number' ? value : new Date(value).getTime();
      return Number.isFinite(time) ? time : null;
    };
    const timeValues = ganttData.flatMap(p =>
      [toTime(p.start), toTime(p.end)].filter((t): t is number => t != null),
    );
    if (timeValues.length === 0) {
      return { min: now - twoWeeks, max: now + twoWeeks };
    }

    const min = Math.min(...timeValues);
    const max = Math.max(...timeValues);
    const range = max - min;

    if (range < oneDay) {
      const center = (min + max) / 2;
      return { min: center - twoWeeks, max: center + twoWeeks };
    }

    const padding = Math.max(oneDay, range * 0.02);
    return { min: min - padding, max: max + padding };
  }
}
